/**
 * masteryEngine.js — Cycle 7 · Learning Science Layer
 * ─────────────────────────────────────────────────────
 * A tiny mastery/spaced-review engine sitting OVER the existing
 * resources (Treasure Quest, Math Academy, Quest Academy). NO new
 * content, activities, or schemas beyond three additive fields on
 * the per-skill progress record: `masteryScore`, `lastSeenAt`,
 * `reviewDueAt`.
 *
 * The engine is deliberately runtime-config-driven (see
 * learningConfig.js) so an admin can retune thresholds without a
 * rebuild.
 *
 * Public API:
 *   loadSkillProgress(skill)          → { masteryScore, lastSeenAt, reviewDueAt }
 *   recordAttempt(skill, opts)        → new score + reviewDueAt after this attempt
 *   isMasteredNow(skill)              → boolean
 *   nextBestActivities({ n })         → array of {source,label,strand,skill,why,path}
 *   ensurePlacement(strands)          → seed masteryScore per strand on first entry
 */
import { recordSkillEvent } from "@/lib/skillLedger";
import { getConfig, getReviewIntervalDays, getDependencyWeight } from "@/lib/learningConfig";
import { getWhyLine } from "@/lib/whyLines";
import { recordEvent as recordProgressEvent, addStars } from "@/lib/progress";

const LS_KEY = "we_mastery_progress_v1";
const LS_LAST_INTERLEAVE = "we_last_strand_v1";
const LS_REFLECTIONS = "we_reflections_v1";
const LS_MASTERED_COUNTS = "we_mastered_counts_v1";

function _load() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); }
  catch { return {}; }
}
function _save(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); }
  catch { /* ignore */ }
}

export function loadSkillProgress(skill) {
  const s = _load();
  return s[skill] || {
    masteryScore: 0,
    lastSeenAt: 0,
    reviewDueAt: 0,
    dependencyWeight: getDependencyWeight(skill),
    attempts: 0,
  };
}

/** Record an attempt on a skill. `firstTry`=first attempt in this
 *  session for this skill (gets a bigger gain). Returns the updated
 *  progress row.
 *
 *  Feb 2026 · Emergent Command · Learning Science Spine — gains are
 *  multiplied by the skill's dependencyWeight so foundational skills
 *  (phonics, phonemic awareness, number sense, place value…) mature
 *  faster AND their reviews come back sooner because a small
 *  proportion of the schedule ceiling is anchored to weight. */
export function recordAttempt(skill, { correct, firstTry = false, strand = null } = {}) {
  // Unified evidence (Oct 2026): every attempt also lands in the canonical skill ledger so parent/teacher
  // summaries, spaced review and recommendations read ONE record. Non-fatal if storage is unavailable.
  try { recordSkillEvent({ skillId: skill, correct: !!correct, kind: "practice", skillType: strand || "academy", meta: { firstTry: !!firstTry } }); } catch { /* ledger is best-effort */ }
  const cfg = getConfig();
  const state = _load();
  const weight = getDependencyWeight(skill);
  const row = state[skill] || {
    masteryScore: 0,
    lastSeenAt: 0,
    reviewDueAt: 0,
    dependencyWeight: weight,
    attempts: 0,
  };
  const baseGain = firstTry ? cfg.correctGainFirstTry : cfg.correctGainNormal;
  const gain = Math.round(baseGain * weight);
  const penalty = Math.round(cfg.incorrectPenalty * Math.max(1, weight * 0.75));
  const delta = correct ? gain : -penalty;
  const next = Math.max(0, Math.min(100, row.masteryScore + delta));
  row.masteryScore = next;
  row.attempts = (row.attempts || 0) + 1;
  row.lastSeenAt = Date.now();
  row.dependencyWeight = weight;
  // Schedule the next spaced-review checkpoint using the current
  // interval index (grows with each successful review, resets on
  // fail). High-dependency skills use a shorter effective interval
  // so they spiral back more often.
  const intervals = getReviewIntervalDays();
  row.reviewIntervalIdx = correct
    ? Math.min(intervals.length - 1, (row.reviewIntervalIdx ?? -1) + 1)
    : 0;
  const rawDays = intervals[row.reviewIntervalIdx] || 1;
  const days = Math.max(1, Math.round(rawDays / Math.max(1, weight)));
  row.reviewDueAt = Date.now() + days * 24 * 3600 * 1000;
  if (strand) row.strand = strand;
  state[skill] = row;
  _save(state);
  // Feb 2026 · Bug #11 fix — Academy mastery signals must feed the
  // Parent Progress Dashboard so a child who masters skills in Quest
  // Academy no longer reads as "zero learning". We cross-wire into
  // the progress store here (single choke-point for every mastery
  // attempt) and fire a "quiz" event on a correct attempt so the
  // dashboard's Stars / Quizzes counters + the "First Quiz Done"
  // badge trigger reliably. Silent on failure.
  try {
    if (correct) {
      recordProgressEvent("quiz", `Mastery attempt: ${skill}`);
      addStars(1);
    }
  } catch { /* ignore */ }
  // Feb 2026 · Passive Gap Learning — emit an event so gapTracker
  // can lazy-reconcile without introducing a hard import cycle.
  if (correct) {
    try { window.dispatchEvent(new CustomEvent("we-mastery-attempt", { detail: { skill, masteryScore: row.masteryScore } })); }
    catch { /* ignore */ }
  }
  return row;
}

// Legacy `mastery.recordAttempt(code, score, total)` also needs to
// feed the Progress Dashboard so lesson-completion counts don't
// remain 0 when a child clicks "I've learned this". Wired in
// `/lib/mastery.js` itself — see that file's Feb 2026 change.

export function isMasteredNow(skill) {
  const cfg = getConfig();
  const row = loadSkillProgress(skill);
  // High-dependency skills require a slightly deeper attempt window
  // before we let progression flow past them.
  const windowBoost = Math.max(0, Math.round((row.dependencyWeight - 1) * cfg.masteryWindow));
  const window = cfg.masteryWindow + windowBoost;
  return row.masteryScore >= cfg.masteryThreshold && row.attempts >= window;
}

/** Placement: on very first entry, seed each strand's masteryScore
 *  from the existing early-question success rate (client-side we
 *  just pick a sensible default — real placement lives in the
 *  activities themselves). */
export function ensurePlacement(strands = ["arithmetic", "reading-writing", "applied"]) {
  const state = _load();
  let touched = false;
  strands.forEach((s) => {
    if (!state[`__placement__${s}`]) {
      state[`__placement__${s}`] = { seededAt: Date.now(), masteryScore: 20 };
      touched = true;
    }
  });
  if (touched) _save(state);
  return state;
}

/** Return the current strand-level rollup for the small mastery bars
 *  on Today's Path. */
export function strandMastery() {
  const state = _load();
  const buckets = {};
  Object.entries(state).forEach(([k, v]) => {
    if (k.startsWith("__placement__")) return;
    const strand = v.strand || "applied";
    (buckets[strand] = buckets[strand] || []).push(v.masteryScore || 0);
  });
  const out = {};
  Object.entries(buckets).forEach(([s, arr]) => {
    out[s] = arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0;
  });
  return out;
}

/** Suggest the next N activities: mix new (lowest-mastery unmastered)
 *  and due reviews (reviewDueAt < now). Interleave strands so we
 *  never serve `cfg.interleaveMax + 1` from the same strand in a row. */
export function nextBestActivities({ n = null } = {}) {
  const cfg = getConfig();
  const count = n || cfg.pathLength;
  const state = _load();
  const rows = Object.entries(state)
    .filter(([k]) => !k.startsWith("__placement__"))
    .map(([skill, v]) => ({ skill, ...v }));

  const dueReviews = rows
    .filter((r) => r.reviewDueAt && r.reviewDueAt <= Date.now())
    // Serve high-dependency reviews first — they anchor the spiral.
    .sort((a, b) => {
      const wa = a.dependencyWeight || 1;
      const wb = b.dependencyWeight || 1;
      if (wa !== wb) return wb - wa;
      return a.reviewDueAt - b.reviewDueAt;
    });

  const newWork = rows
    .filter((r) => (r.masteryScore || 0) < cfg.masteryThreshold)
    .filter((r) => !dueReviews.find((d) => d.skill === r.skill))
    // Weight-adjusted priority: (low mastery × high dependency) first.
    .sort((a, b) => {
      const pa = (100 - (a.masteryScore || 0)) * (a.dependencyWeight || 1);
      const pb = (100 - (b.masteryScore || 0)) * (b.dependencyWeight || 1);
      return pb - pa;
    });

  const strandOrder = ["arithmetic", "reading-writing", "applied"];
  const picks = [];
  const strandStreak = { s: null, n: 0 };
  const pool = [...dueReviews.map((r) => ({ ...r, kind: "review" })),
                ...newWork.map((r) => ({ ...r, kind: "new" }))];

  while (picks.length < count && pool.length) {
    let chose = null;
    for (let i = 0; i < pool.length; i += 1) {
      const cand = pool[i];
      const strand = cand.strand || strandOrder[picks.length % strandOrder.length];
      if (strand === strandStreak.s && strandStreak.n >= cfg.interleaveMax) continue;
      chose = { idx: i, cand, strand };
      break;
    }
    if (!chose) chose = { idx: 0, cand: pool[0], strand: pool[0].strand || "applied" };
    pool.splice(chose.idx, 1);
    strandStreak.n = chose.strand === strandStreak.s ? strandStreak.n + 1 : 1;
    strandStreak.s = chose.strand;
    picks.push({
      skill: chose.cand.skill,
      strand: chose.strand,
      kind: chose.cand.kind,
      masteryScore: chose.cand.masteryScore || 0,
      // "why this" motivation line — reuses existing mastery data.
      why: chose.cand.kind === "review"
        ? `Spaced review — keep this skill fresh.`
        : `You're close to mastering ${chose.cand.skill.replace(/-/g, " ")} — finish to unlock the next stop.`,
      // The three existing resources take these slots — the caller
      // maps a strand to its resource URL.
      source: _sourceForStrand(chose.strand, chose.cand.kind),
    });
  }
  // Persist the last strand so the next session interleaves.
  try { localStorage.setItem(LS_LAST_INTERLEAVE, strandStreak.s || ""); }
  catch { /* ignore */ }
  return picks;
}

function _sourceForStrand(strand, kind) {
  // Reviews always route through Treasure Quest (applied practice).
  if (kind === "review") return { label: "Treasure Quest", path: "/treasure-quest" };
  if (strand === "arithmetic") return { label: "Math Academy", path: "/maths-academy" };
  if (strand === "reading-writing") return { label: "Quest Academy", path: "/academy" };
  return { label: "Treasure Quest", path: "/treasure-quest" };
}

// ────────────────────────────────────────────────────────────────
// Feb 2026 · Learning Science Spine · metacognition reflections.
// Every 3-4 activities the caller can prompt a 10-sec "what felt
// easy / what was tricky?" note and store it unscored. We keep the
// last 30 reflections per browser so parents can browse them later
// (a UI surface will consume `listReflections` in a follow-up).
// ────────────────────────────────────────────────────────────────

/** Save a metacognition note. `note` is free text; `tag` is an
 *  optional tag such as `"easy"` / `"tricky"` / `"session"`. */
export function saveReflection({ note = "", tag = "session", skill = null } = {}) {
  try {
    const arr = JSON.parse(localStorage.getItem(LS_REFLECTIONS) || "[]");
    arr.push({
      note: String(note || "").slice(0, 240),
      tag,
      skill,
      at: Date.now(),
    });
    // Keep the last 30 to bound growth.
    while (arr.length > 30) arr.shift();
    localStorage.setItem(LS_REFLECTIONS, JSON.stringify(arr));
    return arr[arr.length - 1];
  } catch { return null; }
}

/** List up to `n` most-recent reflections (newest first). */
export function listReflections(n = 20) {
  try {
    const arr = JSON.parse(localStorage.getItem(LS_REFLECTIONS) || "[]");
    return arr.slice(-n).reverse();
  } catch { return []; }
}

/** Should we prompt for a reflection? True when the child has just
 *  finished a session of 3-4 activities (caller passes the count). */
export function shouldPromptReflection(activitiesThisSession = 0) {
  return activitiesThisSession > 0 && activitiesThisSession % 3 === 0;
}

// ────────────────────────────────────────────────────────────────
// Feb 2026 · Emergent Command · Learning Strategy Upgrades.
// (2) whyLine helper — one-line concept sentence returned to the
//     activity completion handler. Reuses `lib/whyLines.js`.
// (3) Transfer-check ("unfamiliar problem") — after N mastered
//     activities per strand, resurface a mastered skill via a
//     DIFFERENT existing format. Cadence is config-driven.
// ────────────────────────────────────────────────────────────────

/** Return a one-sentence "why" line for the just-completed activity.
 *  Never blank — falls back through skill → subject → generic. */
export function whyLineFor({ skill = "", subject = "" } = {}) {
  const cfg = getConfig();
  if (cfg.whyReveal === false) return null;
  return getWhyLine({ skill, subject });
}

function _loadMasteredCounts() {
  try { return JSON.parse(localStorage.getItem(LS_MASTERED_COUNTS) || "{}"); }
  catch { return {}; }
}
function _saveMasteredCounts(v) {
  try { localStorage.setItem(LS_MASTERED_COUNTS, JSON.stringify(v)); }
  catch { /* ignore */ }
}

/** Note a mastered activity for the given strand. Returns true when
 *  the cadence tick lands (i.e. this is a transfer-check moment). */
export function noteMasteryAndMaybeTransferCheck(strand) {
  if (!strand) return false;
  const cfg = getConfig();
  const every = Math.max(2, cfg.transferCheckEvery || 5);
  const counts = _loadMasteredCounts();
  counts[strand] = (counts[strand] || 0) + 1;
  _saveMasteredCounts(counts);
  return counts[strand] % every === 0;
}

/** Pick a transfer-check activity for the given strand — a mastered
 *  skill re-formatted through a different existing route. Reuses the
 *  same `_sourceForStrand` mapping but swaps "review" → "applied".
 *  Consumers should treat the returned row as the next path stop
 *  with `mode="transfer-check"`. */
export function pickTransferCheck(strand) {
  const rows = Object.entries(_load())
    .map(([skill, v]) => ({ skill, ...v }))
    .filter((r) => r.strand === strand && (r.masteryScore || 0) >= getConfig().masteryThreshold);
  if (!rows.length) return null;
  // Pick the skill mastered longest ago so the check probes durable
  // memory rather than short-term recall.
  rows.sort((a, b) => (a.lastSeenAt || 0) - (b.lastSeenAt || 0));
  const target = rows[0];
  // Always route the check through Treasure Quest so the *format*
  // differs from the strand's home surface (Math Academy / Quest
  // Academy). This satisfies the "different existing format" rule
  // without introducing new content.
  return {
    kind: "transfer-check",
    skill: target.skill,
    strand,
    source: { label: "Treasure Quest", path: "/treasure-quest" },
    reason: "This idea, tried a new way.",
  };
}

/** Apply a soft penalty when a transfer-check is failed, per the
 *  spec's "warmly re-queue" rule (no punishment; just gently drop
 *  the mastery score so the path revisits). */
export function softenAfterTransferFail(skill) {
  if (!skill) return;
  const state = _load();
  const row = state[skill];
  if (!row) return;
  row.masteryScore = Math.max(0, (row.masteryScore || 0) - 12);
  row.reviewDueAt = Date.now() + 24 * 3600 * 1000; // Come back tomorrow.
  _save(state);
}
